import { classes } from "polytype";
import { omit } from "remeda";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  AggregateMetaData,
  DataModel,
  extractId,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import { ContractDetailed } from "../../contract/index.js";
import type { DomainOrderData } from "../../order/index.js";
import { Order } from "../../order/index.js";
import { Project } from "../../project/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import type { HandleField } from "../DomainHandle/index.js";
import { DomainHandleReadable } from "../DomainHandleReadable/index.js";
import type { ProcessState, ProcessType } from "../DomainProcess/index.js";
import { DomainProcess } from "../DomainProcess/index.js";
import type {
  DomainData,
  DomainListItemData,
  DomainListQueryModelData,
} from "./types.js";

export class Domain extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData("domain", "domain");

  public static defaultNameservers = [
    "ns01.agenturserver.de",
    "ns01.agenturserver.it",
    "ns01.agenturserver.co",
  ];

  public static ofId(id: string) {
    return new Domain(id);
  }

  public static findByHostname = provideReact(async (hostname: string) => {
    const domains = await this.query({
      domainSearchName: hostname,
    }).execute();
    return domains.items.find((i) => i.domain === hostname);
  });

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.domain.find(id);
    if (data !== undefined) {
      return new DomainDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const domain = await this.find(id);
    assertObjectFound(domain, "domain", id);
    return domain;
  });

  public static async order(
    domain: string,
    project: Project,
    handleFields: HandleField[],
    authCode?: string,
  ): Promise<Order> {
    const orderData: DomainOrderData = {
      projectId: project.id,
      domain,
      handleData: {
        ownerC: [handleFields.slice(0, 1)[0]!, ...handleFields.slice(1)],
      },
      authCode,
    };
    return await Order.create({ orderType: "domain", orderData });
  }

  public findDetailed = provideReact(
    () => Domain.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<DomainDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Domain.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<DomainDetailed>>;

  public static query = (query: DomainListQueryModelData = {}) => {
    return new DomainListQuery(query);
  };

  public static queryUniqueDomainContacts = provideReact(
    async (query: DomainListQueryModelData = {}) => {
      const domains = await this.query(query).execute();
      const contacts: DomainListItem[] = [];
      for (const domain of domains.items) {
        const name =
          domain.ownerC.current.organization ?? domain.ownerC.current.name;
        const hashAlreadyAdded = !!contacts.find(
          (i) => i.contactHash === domain.contactHash,
        );
        if (!hashAlreadyAdded && !!name) {
          contacts.push(domain);
        }
      }
      return contacts;
    },
  );
  public static async checkRegistrability(domain: string) {
    return await config.behaviors.domain.checkDomainRegistrable(domain);
  }

  public static async checkTransferability(domain: string, authCode?: string) {
    const transferable = await config.behaviors.domain.checkDomainTransferable(
      domain,
      authCode,
    );
    return { domain, ...transferable };
  }

  public static async suggestDomains(
    prompt: string,
    amount?: number,
    tlds?: string[],
  ) {
    return await config.behaviors.domain.getSuggestions(prompt, amount, tlds);
  }

  public async delete(transit?: boolean) {
    await config.behaviors.domain.delete(this.id, transit);
  }

  public async createAuthCode() {
    const response = await config.behaviors.domain.createAuthCode(this.id);
    return response.authCode;
  }

  public async updateNameservers(nameservers: string[]) {
    return await config.behaviors.domain.updateNameservers(
      this.id,
      nameservers,
    );
  }

  public async setNameserversManaged() {
    await config.behaviors.domain.updateNameservers(
      this.id,
      Domain.defaultNameservers,
    );
  }

  public async resendEmail() {
    await config.behaviors.domain.resendEmail(this.id);
  }

  public async updateOwnerContact(contact: HandleField[]) {
    await config.behaviors.domain.updateOwnerContact(this.id, contact);
  }

  public async updateAuthCode(authCode: string) {
    return await config.behaviors.domain.updateAuthCode(this.id, authCode);
  }

  public async abortDomainDeclaration() {
    await config.behaviors.domain.abortDomainDeclaration(this.id);
  }

  public async getContract() {
    const response = await config.behaviors.domain.getDomainContract(this.id);
    return new ContractDetailed(response);
  }
}

export class DomainCommon extends classes(DataModel<DomainData>, Domain) {
  public readonly domain: string;
  public readonly project: Project;
  public readonly deleted: boolean;
  public readonly nameservers: string[];
  public readonly ownerC: DomainHandleReadable;
  public readonly adminC?: DomainHandleReadable;
  public readonly processes: DomainProcess[];
  public readonly usesDefaultNameserver: boolean;
  public readonly contactHash?: string;
  public readonly connected: boolean;
  public readonly transferInAuthCode?: string;

  public constructor(data: DomainData) {
    super([data], [data.domainId]);
    this.domain = data.domain;
    this.project = Project.ofId(data.projectId);
    this.deleted = data.deleted;
    this.connected = data.connected;
    this.nameservers = data.nameservers;
    this.ownerC = new DomainHandleReadable(data.handles.ownerC);
    if (data.handles.adminC) {
      this.adminC = new DomainHandleReadable(data.handles.adminC);
    }
    this.usesDefaultNameserver = data.usesDefaultNameserver;
    this.processes = data.processes
      ? data.processes.map((i) => new DomainProcess(i))
      : [];
    this.contactHash = data.contactHash;
    this.transferInAuthCode = data.transferInAuthCode;
  }

  public hasProcessOfType(type: ProcessType) {
    return this.processes.some((i) => i.processType === type);
  }

  public hasProcessState(state: ProcessState) {
    return this.processes.some((i) => i.state === state);
  }

  public hasProcessWithStateAndType(state: ProcessState, type: ProcessType) {
    return this.processes.some(
      (i) => i.processType === type && i.state === state,
    );
  }

  public hasAuthCodeMismatchError() {
    return this.processes.some((i) =>
      i.error && i.state === "FAILED"
        ? i.error.includes("AuthCodeMismatchError") ||
          i.error.includes("ErrWrongAuthCode") ||
          i.error.includes("AuthInfo does not match")
        : false,
    );
  }

  public getCustomNameservers(): string[] {
    return this.nameservers.filter(
      (i) => !Domain.defaultNameservers.includes(i),
    );
  }
}

export class DomainDetailed extends classes(
  DomainCommon,
  DataModel<DomainData>,
) {
  public constructor(data: DomainData) {
    super([data], [data]);
  }
}

export class DomainListItem extends classes(
  DomainCommon,
  DataModel<DomainListItemData>,
) {
  public constructor(data: DomainListItemData) {
    super([data]);
  }
}

export class DomainListQuery extends ListQueryModel<DomainListQueryModelData> {
  public constructor(data: DomainListQueryModelData = {}) {
    super(data);
  }

  public refine(query: DomainListQueryModelData) {
    return new DomainListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.domain.list({
      ...omit(this.query, ["project"]),
      projectId: extractId(this.query.project),
    });

    return new DomainList(
      this.query,
      items.map((d) => new DomainListItem(d)),
      totalCount,
    );
  }, [this.queryId, "domain"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "domain"]);
}

export class DomainList extends classes(
  DomainListQuery,
  ListDataModel<DomainListItem>,
) {
  public constructor(
    query: DomainListQueryModelData,
    domains: DomainListItem[],
    totalCount: number,
  ) {
    super([query], [domains, totalCount]);
  }
}
